import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Package, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Searchable product picker with server-side typeahead.
 * Searches by name, brand, model, SKU, or serial number.
 *
 * Props:
 *   onSelect(product, matchedSerial?)   - called when user picks a result
 *   placeholder                          - input placeholder
 *   inStockOnly                          - filter to only in-stock products (default false)
 *   testId                               - data-testid prefix
 *   autoFocus                            - focus input on mount
 *   initialValue                         - initial text shown
 */
export const ProductPicker = ({
  onSelect,
  placeholder = 'Search by name, brand, model, SKU, or serial...',
  inStockOnly = false,
  testId = 'product-picker',
  autoFocus = false,
  initialValue = ''
}) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced search
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.searchProducts(q.trim(), { limit: 20, inStockOnly });
      setResults(res.data.results || []);
      setIsOpen(true);
      setHighlightIdx(0);
    } catch (e) {
      console.error('search failed', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [inStockOnly]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => runSearch(query), 200);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [query, runSearch]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const handlePick = (product) => {
    onSelect && onSelect(product, product.matched_serial);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handlePick(results[highlightIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={testId}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          className="w-full pl-9 pr-9 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={`${testId}-input`}
        />
        {loading ? (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />
        ) : query ? (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setIsOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg" data-testid={`${testId}-results`}>
          {results.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePick(p)}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${idx === highlightIdx ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              data-testid={`${testId}-option-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Package size={12} className="text-gray-400 flex-shrink-0" />
                    <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{p.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                    {p.brand && <span>{p.brand}</span>}
                    {p.model && <span>· {p.model}</span>}
                    {p.sku && <span>· SKU: {p.sku}</span>}
                    {p.matched_serial && <span className="font-mono text-blue-600 dark:text-blue-400">· SN: {p.matched_serial.sn}</span>}
                  </div>
                </div>
                <div className="text-right text-xs flex-shrink-0">
                  <div className="font-semibold text-gray-900 dark:text-white">{p.price ? `LKR ${Number(p.price).toLocaleString()}` : '—'}</div>
                  <div className={`mt-0.5 ${p.in_stock_count > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                    {p.in_stock_count > 0 ? `${p.in_stock_count} in stock` : 'Out of stock'}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && !loading && query && results.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-3 text-sm text-gray-500" data-testid={`${testId}-no-results`}>
          No products match &quot;{query}&quot;
        </div>
      )}
    </div>
  );
};
